import type { AppDispatch } from "_shared/_stores/store-types";
import Guid from "_shared/guids/guid";
import { staffingNotOnTeam } from "_shared/fee-schedules/categories/fee-schedule-template-staffing";
import { enqueueSnackbar } from "notistack";
import type { NavigateFunction } from "react-router";
import type Session from "users/__session/session";
import FeeScheduleTemplate from "work/fee-schedule-templates/fee-schedule-template";
import ProposalBuilder from "work/proposals/__builder/proposal-builder";
import {
  replaceOriginalProposalBuilder,
  replaceUpdatedProposalBuilder,
} from "work/proposals/_store/proposals-redux-slice";
import { PageTab } from "work/proposals/_views/drafts/draft-proposal-page-tabs";
import TeamTemplate from "work/team-templates/team-template";
import VendorTeam from "work/work-agreements/teams/vendor-team/vendor-team";

type StartDraftProposalFromTemplateArgs<TTemplate> = {
  dispatch: AppDispatch;
  navigate: NavigateFunction;
  session: Session;
  template: TTemplate;
};

function startDraftProposalFromBuilder(args: {
  builder: ProposalBuilder;
  dispatch: AppDispatch;
  navigate: NavigateFunction;
  pageTab: PageTab;
}) {
  const { builder, dispatch, navigate, pageTab } = args;
  dispatch(replaceOriginalProposalBuilder({ proposalId: Guid.EMPTY, builder }));
  dispatch(replaceUpdatedProposalBuilder({ proposalId: Guid.EMPTY, builder }));
  navigate(`/proposals/drafts/create/${pageTab}`);
}

export function startDraftProposalFromTeamTemplate(args: StartDraftProposalFromTemplateArgs<TeamTemplate>) {
  const { dispatch, navigate, session, template } = args;
  const selectedTeam = new VendorTeam({
    leader: template.leader,
    members: template.members,
    marketplaceId: template.marketplaceId,
  });
  let builder = new ProposalBuilder({ session }).setVendorTeam(selectedTeam);

  if (template.id) {
    builder = session.context?.viewingAsVendor
      ? builder.setVendorTeamTemplateIds([template.id])
      : builder.setClientTeamTemplateIds([template.id]);
  }

  startDraftProposalFromBuilder({
    builder,
    dispatch,
    navigate,
    pageTab: PageTab.VendorTeam,
  });
}

/**
 * Starts a draft from a fee schedule template, keeping who each rate is for.
 *
 * The other two surfaces that apply a template ask what to do about a person the proposal's team
 * does not have, because there they would be changing a team that already exists. Here there is no
 * team at all — this builder is new and nothing has set one — so every name is missing for the same
 * reason and there is nothing to trade away. The people the template names become the draft's
 * vendor team, and the message says so, which is what keeps it from happening silently (#5237).
 *
 * No leader is picked. A draft needs one to be submitted or hired
 * (`WorkAgreement.ValidateFeeSchedule`'s neighbours in that file), and the user picks it on the
 * Vendor Team tab exactly as they did before.
 */
export function startDraftProposalFromFeeScheduleTemplate(
  args: StartDraftProposalFromTemplateArgs<FeeScheduleTemplate>,
) {
  const { dispatch, navigate, session, template } = args;
  let builder = new ProposalBuilder({ session }).setFeeSchedule([...template.categories]);

  const staffing = staffingNotOnTeam(template.categories, []);
  if (staffing.length > 0) {
    builder = builder.setVendorTeam(new VendorTeam({ members: staffing }));
    enqueueSnackbar(
      staffing.length === 1
        ? "Added the person this fee schedule names to the vendor team."
        : `Added the ${staffing.length} people this fee schedule names to the vendor team.`,
      { variant: "info" },
    );
  }

  if (template.id) {
    builder = session.context?.viewingAsVendor
      ? builder.setVendorFeeScheduleTemplateIds([template.id])
      : builder.setClientFeeScheduleTemplateIds([template.id]);
  }

  startDraftProposalFromBuilder({
    builder,
    dispatch,
    navigate,
    pageTab: PageTab.FeeSchedule,
  });
}
